import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, signal } from '@angular/core';
import { ClientInformation } from 'src/app/models/client-information';
import { Integration } from 'src/app/models/integration';
import { IntegrationInformation } from 'src/app/models/integration-information';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { IdentifierService } from 'src/app/services/identifier.service';
import { ToastService } from 'src/app/services/toast.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { DynamicDropdownViewComponent } from '../dynamic-dropdown-view/dynamic-dropdown-view.component';
import { LTSpinnerComponent } from 'src/app/features/lt-shared-ui/lt-info/components/lt-spinner/lt-spinner.component';

@Component({
  selector: 'app-integration-dropdown-view',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    DynamicDropdownViewComponent,
    LTSpinnerComponent,
  ],
  templateUrl: './integration-dropdown-view.component.html',
  styleUrl: './integration-dropdown-view.component.scss',
})
export class IntegrationDropdownViewComponent implements OnInit, OnDestroy {
  destroyed$: Subject<boolean> = new Subject();
    @Input() items: Integration[] = [];
    @Input() infos: IntegrationInformation[] = [];
    @Input() client!: ClientInformation;
    @Input() onlyDataSwap = false;
    @Input() onlyFastFetch = false;
    @Input() onlyUseInfo = false;
    @Input() labelName = 'Integration';
    @Input() noItemsMessage = 'No integrations available.';

    @Output() itemSelected = new EventEmitter<Integration | null>();
    @Output() loadingFailed = new EventEmitter<any>();

    @Input() overrideId: string = null;
    @Output() itemSelectedFromOverride = new EventEmitter<any>();

    selectedItem = signal<Integration | null>(null);

    hasItems = false;

    constructor(
      private readonly clientService: ClientIntegrationService,
      private readonly idService: IdentifierService,
      protected toastService: ToastService,
    ) { }

    ngOnInit(): void {
      this.getIntegrations();
    }

    ngOnDestroy(): void {
      this.destroyed$.next(true);
      this.destroyed$.complete();
    }

    getIntegrations(): void {
      this.hasItems = false;
      if (this.items != null && this.items.length > 0)
      {
        this.hasItems = true;
      }
      else if (this.infos != null && this.infos.length > 0)
      {
        this.items = this.infos.map(x => new Integration(x));
        this.hasItems = true;
      }
      else {
        if (this.onlyDataSwap || this.onlyFastFetch || this.onlyUseInfo)
        {
          this.getData()
          .pipe(takeUntil(this.destroyed$))
          .subscribe((data) => {
            if (data == null || data.length === 0)
            {
              this.loadingFailed.emit();
            }
            else {
              const filtered = data.filter(x => x.clientId == this.client.losTalkerClientId);
              if (filtered == null || filtered.length == 0) {
                this.loadingFailed.emit();
              }
              else {
                let clientIntegrations = filtered.map(x => new Integration(x));
                clientIntegrations = clientIntegrations.filter((v, i, a) => a.map(x => x.integrationId).indexOf(v.integrationId) === i);
                this.items = clientIntegrations;
                this.hasItems = true;
              }
            }
          });
        }
        else if (this.client != null){
          this.clientService.getIntegrationsByClient(this.client.clientDisplayName)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((data) => {
            this.items = data;
            this.hasItems = true;
          });
        }

      }
    }

    public getData(): Observable<IntegrationInformation[]>{
      if (this.onlyDataSwap)
      {
        return this.idService.getAllActiveIntegrationIdentifiersForDataSwap();
      }
      else if (this.onlyFastFetch)
      {
        return this.idService.getAllActiveIntegrationIdentifiersForFastFetch();
      }
      else {
        //TODO LT-44652 Currently this does the same thing as getAllActiveIntegrationIdentifiersForDataSwap, which doesn't get all identifiers 
        return this.idService.getAllActiveIntegrationInformation();
      }
    }

  editItem(): void {
    this.selectedItem.set(null);
    this.itemSelected.emit(null);
  }

    selectItemFromOverride(item: Integration): void {
      this.selectedItem.set(item);
      this.itemSelectedFromOverride.emit(this.selectedItem());
    }

    selectItem(item: Integration): void {
      this.selectedItem.set(item);
      this.itemSelected.emit(this.selectedItem());
    }

    public displayItem(item: Integration): string {
      return item?.name ?? '';
    }
}
